import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Typography,
  Paper,
  Pagination,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import dayjs from "dayjs";
import { DividendCompanyStats, PaginationInfo } from "../Charts/hooks/useDividendAnalysis";

interface Column {
  id: keyof DividendCompanyStats | 'company';
  label: string;
  align?: 'left' | 'right' | 'center';
  format?: (value: any, company: DividendCompanyStats, currencySymbol?: string) => React.ReactNode;
}

const getColumns = (currencySymbol: string): Column[] => [
  {
    id: 'company',
    label: 'Company',
    align: 'left',
    format: (_, company) => (
      <Box display="flex" alignItems="center" gap={1.5}>
        <Box sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Avatar
            src={company.logo}
            alt={company.ticker}
            sx={{
              width: 28,
              height: 28,
              background: "transparent",
            }}
          />
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {company.ticker}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, fontSize: '0.75rem' }}>
            {company.companyName}
          </Typography>
        </Box>
      </Box>
    ),
  },
  {
    id: 'totalAmount',
    label: 'Total Received',
    align: 'right',
    format: (value) => (
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {currencySymbol}{Number(value).toFixed(2)}
      </Typography>
    ),
  },
  {
    id: 'paymentCount',
    label: 'Payments',
    align: 'center',
    format: (value) => (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    ),
  },
  {
    id: 'firstPaymentDate',
    label: 'First Payment',
    align: 'right',
    format: (value) => (
      <Typography variant="body2">
        {dayjs(value).format("MMM DD, YYYY")}
      </Typography>
    ),
  },
  {
    id: 'lastPaymentDate',
    label: 'Last Payment',
    align: 'right',
    format: (value) => (
      <Typography variant="body2">
        {dayjs(value).format("MMM DD, YYYY")}
      </Typography>
    ),
  },
];

interface DividendStatsTableProps {
  companies: DividendCompanyStats[];
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  isLoading?: boolean;
}

const DividendStatsTable: React.FC<DividendStatsTableProps> = ({
  companies,
  pagination,
  onPageChange,
  onLimitChange,
  isLoading = false,
}) => {
  const currencySymbol = "$";
  const columns = getColumns(currencySymbol);
  
  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
              : "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
          border: (theme) =>
            theme.palette.mode === "dark"
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid rgba(0,0,0,0.05)",
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(0,0,0,0.02)",
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      borderBottom: (theme) =>
                        theme.palette.mode === "dark"
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "1px solid rgba(0,0,0,0.08)",
                      py: 1.5,
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      {isLoading ? 'Loading dividend statistics...' : 'No dividend payments found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((company, index) => (
                  <TableRow
                    key={company.ticker}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.05)"
                            : "rgba(0,0,0,0.04)",
                      },
                      '&:last-child td': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          borderBottom: (theme) =>
                            theme.palette.mode === "dark"
                              ? "1px solid rgba(255,255,255,0.05)"
                              : "1px solid rgba(0,0,0,0.05)",
                          py: 1,
                        }}
                      >
                        {column.format 
                          ? column.format(company[column.id as keyof DividendCompanyStats], company)
                          : company[column.id as keyof DividendCompanyStats]
                        }
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {pagination && (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {pagination.totalPayments > 0 
                ? ((pagination.currentPage - 1) * pagination.limit + 1)
                : 0} - {Math.min(
                  pagination.currentPage * pagination.limit,
                  pagination.totalPayments
                )} of {pagination.totalPayments} companies
            </Typography>

            <Box display="flex" alignItems="center" gap={2}>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={pagination.limit}
                  onChange={(e) => onLimitChange(Number(e.target.value))}
                  sx={{
                    '& .MuiSelect-select': {
                      py: 0.5,
                      fontSize: '0.875rem',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.2)"
                          : "rgba(0,0,0,0.2)",
                    },
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
              
              <Typography variant="body2" color="text.secondary">
                per page
              </Typography>

              {pagination.totalPages > 1 && (
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={(_, page) => onPageChange(page)}
                  color="primary"
                  showFirstButton
                  showLastButton
                  size="small"
                  sx={{
                    ml: 1,
                    '& .MuiPagination-ul': {
                      flexWrap: 'nowrap',
                    },
                    '& .MuiPaginationItem-root': {
                      borderRadius: 2,
                      fontWeight: 500,
                      '&.Mui-selected': {
                        background: (theme) =>
                          theme.palette.mode === "dark"
                            ? "linear-gradient(135deg, rgba(34, 197, 94, 0.8) 0%, rgba(22, 163, 74, 0.7) 100%)"
                            : "linear-gradient(135deg, rgba(22, 163, 74, 0.9) 0%, rgba(34, 197, 94, 0.8) 100%)",
                        color: "#ffffff",
                      },
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DividendStatsTable;